import { Card, CardContent, CardHeader, Divider, FormControl, FormControlLabel, FormHelperText, Grid2, IconButton, Stack, Switch } from "@mui/material"
import DashboardLayout from "../../../layoutcontainers/DashboardLayout"
import { AccountBalance, Add, ArrowBack, Contacts, Delete } from "@mui/icons-material"
import MaterialTypography from "../../../components/MaterialTypography"
import { useNavigate, useSearchParams } from "react-router-dom"
import MaterialButton from "../../../components/MaterialButton"
import MaterialBox from "../../../components/MaterialBox"
import MaterialInput from "../../../components/MaterialInput"
import { useBranchFormHandling } from "./components/BranchStateHandle"
import MaterialSelect from "../../../components/MasterSelect"
import { BsFillPersonVcardFill } from "react-icons/bs"
import { useEffect, useState } from "react"
import { fetchData, fetchDataById, postData, putData } from "../../../services"
import { contactTypeOptions } from "./components/BranchData"
import { useLoading } from "../../../components/Loading"
import { useSnackbar } from "../../../context/popup"

const BankBranchForm = () => {

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { showSnackbar } = useSnackbar();
    const { setCircleLoading } = useLoading();

    const bankBranchId = searchParams.get("id");
    const pageType = searchParams.get("pageType");
    const pageMode = pageType === "view"
        ? "view"
        : pageType === "edit"
        ? "edit"
        : pageType === "add"
            ? "add"
            : "add";
    const { formData, setFormData, contacts, setContacts, handleChange, handleContactChange, checkAllFields } = useBranchFormHandling();
    
    const [bankOptions, setBankOptions] = useState([]);
    const [countryOptions, setCountryOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);
    const [districtOptions, setDistrictOptions] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);
    const [pincodeOptions, setPincodeOptions] = useState([]);

    //Bank and Country Dropdown API UseEffect
    useEffect(() => {
        if (pageMode !== "view") {        
            fetchAllDropdowns();
        }
    }, [pageMode]);

    //Fetch data by Pincode Id UseEffect
    useEffect(() => {
        if (pageMode === "add" || !bankBranchId) {
            return;
        }        
        fetchBankBranchById(bankBranchId);
    }, [pageMode]);

    //State Dropdown API UseEffect
    useEffect(() => {
        if (!formData.countryId?.value || pageMode === "view") return;
        
        fetchStateDropdown(formData.countryId.value);
    }, [formData.countryId?.value]);

    //District Dropdown API UseEffect
    useEffect(() => {
        if (!formData.stateId?.value || pageMode === "view") return;
        
        fetchDistrictDropdown();
    }, [formData.stateId?.value]);

    //City Dropdown API UseEffect
    useEffect(() => {
        if (!formData.districtId?.value || pageMode === "view") return;
        
        fetchCityDropdown();
    }, [formData.districtId?.value]);

    //Pincode Dropdown API UseEffect
    useEffect(() => {
        if (!formData.cityId?.value || pageMode === "view") return;
        
        fetchPincodeDropdown();
    }, [formData.cityId?.value]);

    //Bank and Country Dropdown API
    const fetchAllDropdowns = async () => {
        setCircleLoading(true);
        try {
            const [countryRes, bankRes] = await Promise.all([
                fetchData(`/reference/general/country?dropdown=true&isActive=true`),
                fetchData(`/reference/general/bank?dropdown=true&isActive=true`)
            ]);

            // Country Dropdown
            if(countryRes?.success){
                const formattedCountries = countryRes.data.items.map((item) => ({
                    value: item.id,
                    label: item.displayName,
                }));
                setCountryOptions(formattedCountries);
            } 
            else{
                showSnackbar("Error fetching country data", "error");
            }

            // Bank Dropdown
            if (bankRes?.success) {
                const formattedBanks = bankRes.data.items.map((item) => ({
                    value: item.id,
                    label: item.name,
                }));
                setBankOptions(formattedBanks);
            } 
            else{
                showSnackbar("Error fetching bank data", "error");
            }

        } 
        catch(error){
            console.error("Error fetching dropdowns:", error);
            showSnackbar("Fetching failed", "error");

        }
        finally{
            setCircleLoading(false);
        }
    };

    //State Dropdown API
    const fetchStateDropdown = async (countryId) => {
        try {
        const response = await fetchData(`/reference/general/state?dropdown=true&isActive=true&countryId=${countryId}`)
        
        if(response?.success){
            const formattedStates = response?.data?.items.map((item) => ({
            value: item.id,
            label: item?.name
            }))
            setStateOptions(formattedStates);
        }
        else{
            //throw error
        }
        }       
        catch (error) {
        console.error('Error Fetching States:', error);
        }
    };

    //District Dropdown API
    const fetchDistrictDropdown = async () => {
        try {
        const response = await fetchData(`/reference/general/district?dropdown=true&isActive=true&stateId=${formData.stateId?.value}`)
        
        if(response?.success){
            const formattedDistricts = response?.data?.items.map((item) => ({
                value: item.id,
                label: item?.name
            }))
            setDistrictOptions(formattedDistricts);
        }
        else{
            //throw error
        }
        } catch (error) {
        console.error('Error Fetching Districts:', error);
        showSnackbar(error?.response?.data?.message || "Error Fetching Districts","error");
        }
    }

    //City Dropdown API
    const fetchCityDropdown = async () => {
        try {
        const response = await fetchData(`/reference/general/city?dropdown=true&isActive=true&districtId=${formData.districtId?.value}`)
        
        if(response?.success){
            const formattedCities = response?.data?.items.map((item) => ({
                value: item.id,
                label: item?.name
            }))
            setCityOptions(formattedCities);
        }
        else{
            //throw error
        }
        } catch (error) {
        console.error('Error Fetching Cities:', error);
        }
    }

    //Pincode Dropdown API
    const fetchPincodeDropdown = async () => {
        try {
        const response = await fetchData(`/reference/general/pincode?dropdown=true&isActive=true&cityId=${formData.cityId?.value}`)
        
        if(response?.success){
            const formattedPincodes = response?.data?.items.map((item) => ({
                value: item.id,
                label: item?.pincode
            }))
            setPincodeOptions(formattedPincodes);
        }
        else{
            //throw error
        }
        } catch (error) {
        console.error('Error Fetching Pincodes:', error);
        }
    }

    //Fetch data by Pincode Id
    const fetchBankBranchById = async (bankBranchId) => {
        try {
        setCircleLoading(true);
        const response = await fetchDataById(`/reference/general/branch/${bankBranchId}`);

        const responseData = response?.data;
        
        if (response.success) {
            const mappedFormData = {
                name: responseData.branch?.name || "",
                code: responseData.branch?.code || "",
                bankId: {value: responseData?.branch?.bank?.id, label: responseData?.branch?.name},
                countryId : {value: responseData.address?.city?.district?.state?.country?.id, label: responseData.address?.city?.district?.state?.country?.displayName},
                stateId: {value: responseData.address?.city?.district?.state?.id, label: responseData.address?.city?.district?.state?.name},
                districtId: {value: responseData.address?.city?.district?.id, label: responseData.address?.city?.district?.name},
                cityId: {value: responseData.address?.city?.id, label: responseData.address?.city?.name},
                pinCodeId: {value: responseData.address?.pinCode?.id, label: responseData.address?.pinCode?.code},
                addressLine1: responseData.address?.addressLine1 || "",
                addressLine2: responseData.address?.addressLine2 || "",
                landmark: responseData.address?.landmark || "",
                active: responseData.branch?.isActive,

                // reset statuses (edit mode)
                nameStatus: responseData.branch?.name ? "success" : "unchecked",
                codeStatus: responseData.branch?.code ? "success" : "unchecked",
                bankIdStatus: responseData.branch?.bank?.id ? "success" : "unchecked",
                countryIdStatus: responseData.address?.city?.district?.state?.country?.id ? "success" : "unchecked",
                stateIdStatus: responseData.address?.city?.district?.state?.id ? "success" : "unchecked",
                districtIdStatus: responseData.address?.city?.district?.id ? "success" : "unchecked",
                cityIdStatus: responseData.address?.city?.id ? "success" : "unchecked",
                pinCodeIdStatus: responseData.address?.pinCodeId ? "success" : "unchecked",
                addressLine1Status: responseData.address?.addressLine1 ? "success" : "unchecked",
                addressLine2Status: responseData.address?.addressLine2 ? "success" : "unchecked",
                landmarkStatus: responseData.address?.landmark ? "success" : "unchecked",
            };

            setFormData(mappedFormData);
            const mappedContacts = responseData.contacts?.length > 0
                    ? responseData.contacts.map((contact) => {
                        const matchedOption = contactTypeOptions.find(
                        (opt) => opt.value === contact.contactType
                        );

                        return {
                        id: contact.id || "",
                        contactType: matchedOption || null,
                        value: contact.value || "",
                        primary: contact.primary || false,
                        isActive: contact.isActive ?? true,

                        contactTypeStatus: matchedOption ? "success" : "unchecked",
                        valueStatus: contact.value ? "success" : "unchecked",
                        };
                    })
                    : [
                        {
                        id: "",
                        contactType: null,
                        value: "",
                        primary: false,
                        isActive: true,
                        contactTypeStatus: "unchecked",
                        valueStatus: "unchecked",
                        },
                    ];

                setContacts(mappedContacts);

        }
        } catch (error) {
        console.error("Error fetching data by ID:", error);
        showSnackbar(error?.response?.data?.message || "Network Error","error");
        }finally{
            setCircleLoading(false);
        }
    };

    //post and put api function
    const handleSubmitBankBranch = async () => {
        const payload = {
            branch: {
                bankId: formData?.bankId?.value || "",
                name: formData?.name || "",
                code: formData?.code || "",
                isActive: formData?.active
            },
            address: {
                addressLine1: formData?.addressLine1 || "",
                addressLine2: formData?.addressLine2 || "",
                landmark: formData?.landmark || "",
                pinCodeId: formData?.pinCodeId?.value || "",
                cityId: formData?.cityId?.value || "",
                latitude: null, 
                longitude: null
            },
        };
        //this payload is sent only in POST not in PUT
        if(!bankBranchId) {
            payload.contacts = contacts.map((c) => ({
                value: c?.value || "",
                contactType: c?.contactType?.value || "",
                primary: c?.primary,
                isActive: c?.isActive,
            }));
        }

        //Update
        if(bankBranchId){
            try {
                const response = await putData(`/reference/general/branch/${bankBranchId}`, payload);
                const responseMessage = response?.message;
                if(response?.success){
                //success message
                    showSnackbar(responseMessage,"success");
                    navigate('/general-master/bank-branch-list');          
                }
                else{
                    //error message
                    showSnackbar(responseMessage,"error")
                }
            } catch (error) {
                console.error('Error updating data:', error);
                showSnackbar(error?.response?.data?.message || 'Error updating data', "error");
            }
        }
        else{      
        //Submit
            try {
                const response = await postData('/reference/general/branch', payload);
                const responseMessage = response?.message;
                if(response?.success){
                    //success message
                    showSnackbar(responseMessage,"success");
                    navigate('/general-master/bank-branch-list');          
                }
                else{
                    //error message
                    showSnackbar(responseMessage,"error")
                }
            } catch (error) {
                console.error('Error posting data:', error);
                showSnackbar(error?.response?.data?.message || 'Error Adding Data', "error");
            }
        }
    };

    const handleAddContact = () => {
        setContacts([
            ...contacts,
            {
            contactType: "",
            value: "",
            primary: false,
            isActive: true,
            contactTypeStatus: "unchecked",
            valueStatus: "unchecked",
            },
        ]);
    };

    const handleRemoveContact = (index) => {
        const updatedContacts = contacts.filter((_, i) => i !== index);
        setContacts(updatedContacts);
    };

  return (
    <DashboardLayout>
        <Card sx={{minHeight: 650}}>
            <CardHeader
            title={(
                <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                >
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <IconButton onClick={() => navigate("/general-master/bank-branch-list")}>
                        <ArrowBack />
                        </IconButton>
                        <Stack direction="column" spacing={0}>
                        <MaterialTypography variant="h5" fontWeight="medium">
                            Bank Branch Details
                        </MaterialTypography>
                        <MaterialTypography variant="caption" color="text">
                            Enter New Bank Branch Details respective to the Bank.
                        </MaterialTypography>
                        </Stack>
                    </Stack>
                </Stack>
            )}
            />
            <CardContent>
                <Stack direction="column">
                    <Stack direction="row" spacing={1} >
                        <AccountBalance style={{ fontSize: 20, marginTop: 2 }} />
                        <MaterialTypography
                            component="label"
                            variant="body2"
                            fontWeight="bold">
                            Bank Information
                        </MaterialTypography>
                    </Stack>
                    <MaterialTypography
                    ml={3.5}
                    variant="caption" color="text">
                        Enter Bank Branch Details according to Bank
                    </MaterialTypography>
                </Stack>
                <Grid2 container spacing={1}>
                    <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                        <MaterialBox>
                            <MaterialTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold">
                                Branch Name*
                            </MaterialTypography>
                        </MaterialBox>
                        <MaterialInput
                        name="name"
                        placeholder="eg. Selaiyur"
                        value={formData.name}
                        onChange={handleChange}
                        success={formData.nameStatus === "success"}
                        error={formData.nameStatus === "error"}
                        disabled={pageMode === "view"}
                        />
                        {formData.nameStatus === "error" && (
                            <FormControl error>
                                <FormHelperText>
                                Branch Name should be at least 1 character and at most 80 characters long.
                                </FormHelperText>
                            </FormControl>
                        )}
                    </Grid2>
                    <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                        <MaterialBox>
                            <MaterialTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold">
                                Branch Code*
                            </MaterialTypography>
                        </MaterialBox>
                        <MaterialInput
                        name="code"
                        placeholder="BRAN100"
                        value={formData.code}
                        onChange={handleChange}
                        success={formData.codeStatus === "success"}
                        error={formData.codeStatus === "error"}
                        disabled={pageMode === "view"}
                        />
                        {formData.codeStatus === "error" && (
                            <FormControl error>
                                <FormHelperText>
                                Branch Code should be at least 1 character and at most 30 characters long.
                                </FormHelperText>
                            </FormControl>
                        )}
                    </Grid2>
                    <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                        <MaterialBox>
                            <MaterialTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold">
                                Bank Name*
                            </MaterialTypography>
                        </MaterialBox>
                        <MaterialSelect
                        name="bankId"
                        value={formData.bankId}
                        onChange={handleChange}
                        options={bankOptions}
                        success={formData.bankIdStatus === "success"}
                        error={formData.bankIdStatus === "error"}
                        isDisabled={pageMode === "view"}
                        />
                    </Grid2>
                    <Grid2 size={{lg:2, md:2, sm: 2, xs: 2 }}>
                        <MaterialBox>
                            <MaterialTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold">
                                Active
                            </MaterialTypography>
                        </MaterialBox>
                        <FormControlLabel
                        control={
                            <Switch
                                checked={formData.active}
                                disabled={pageMode === "view"}
                                onChange={(e) =>
                                    handleChange({
                                        target: {
                                            name: "active",
                                            value: e.target.checked,
                                        },
                                    })
                                }
                                color="primary"
                            />
                        }
                        label={
                            <MaterialTypography variant="caption" fontWeight="bold">
                                {formData?.active ? "Yes" : "No"}
                            </MaterialTypography>
                        }
                        sx={{ marginLeft: 0 }}
                        />
                    </Grid2>
                </Grid2>
                <Divider/>
                <Stack direction="column">
                    <Stack direction="row" spacing={1}>
                        <BsFillPersonVcardFill style={{ fontSize: 20 }} />
                        <MaterialTypography
                            component="label"
                            variant="body2"
                            fontWeight="bold">
                            Address Information
                        </MaterialTypography>                       
                    </Stack>
                    <MaterialTypography
                    ml={3.5}
                    variant="caption" color="text">
                        Enter Detailed Address Information with Country, State, District, City, PIN Code and Address Line 1
                    </MaterialTypography>
                </Stack>
                <Grid2 container spacing={1} mt={2}>
                    <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                        <MaterialBox>
                            <MaterialTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold">
                                Country Name*
                            </MaterialTypography>
                        </MaterialBox>
                        <MaterialSelect
                        name="countryId"
                        value={formData.countryId}
                        onChange={handleChange}
                        options={countryOptions}
                        success={formData.countryIdStatus === "success"}
                        error={formData.countryIdStatus === "error"}
                        isDisabled={pageMode === "view"}
                        />
                    </Grid2>
                    <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                        <MaterialBox>
                            <MaterialTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold">
                                State Name*
                            </MaterialTypography>
                        </MaterialBox>
                        <MaterialSelect
                        name="stateId"
                        options={stateOptions}
                        value={formData.stateId}
                        onChange={handleChange}
                        success={formData.stateIdStatus === "success"}
                        error={formData.stateIdStatus === "error"}
                        isDisabled={formData.countryId === "" || pageMode === "view"}
                        />
                    </Grid2> 
                    <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                        <MaterialBox>
                            <MaterialTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold">
                                District Name*
                            </MaterialTypography>
                        </MaterialBox>
                        <MaterialSelect
                        name="districtId"
                        options={districtOptions}
                        value={formData.districtId}
                        onChange={handleChange}
                        success={formData.districtIdStatus === "success"}
                        error={formData.districtIdStatus === "error"}
                        isDisabled={formData.stateId === "" || pageMode === "view"}
                        menuPlacement="top"
                        />
                    </Grid2> 
                    <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                        <MaterialBox>
                            <MaterialTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold">
                                City Name*
                            </MaterialTypography>
                        </MaterialBox>
                        <MaterialSelect
                        name="cityId"
                        options={cityOptions}
                        value={formData.cityId}
                        onChange={handleChange}
                        success={formData.cityIdStatus === "success"}
                        error={formData.cityIdStatus === "error"}
                        isDisabled={formData.districtId === "" || pageMode === "view"}
                        menuPlacement="top"
                        />
                    </Grid2>
                    <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                        <MaterialBox>
                            <MaterialTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold">
                                Pin Code*
                            </MaterialTypography>
                        </MaterialBox>
                        <MaterialSelect
                        name="pinCodeId"
                        options={pincodeOptions}
                        value={formData.pinCodeId}
                        onChange={handleChange}
                        success={formData.pinCodeIdStatus === "success"}
                        error={formData.pinCodeIdStatus === "error"}
                        isDisabled={formData.cityId === "" || pageMode === "view"}
                        menuPlacement="top"
                        />
                    </Grid2>                    
                    <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                        <MaterialBox>
                            <MaterialTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold">
                            Address Line 1*
                            </MaterialTypography>
                        </MaterialBox>
                        <MaterialInput
                        name="addressLine1"
                        placeholder="eg. No.12, Ram Nagar"
                        value={formData.addressLine1}
                        onChange={handleChange}
                        success={formData.addressLine1Status === "success"}
                        error={formData.addressLine1Status === "error"}
                        disabled={pageMode === "view"}
                        />
                        {
                          formData.addressLine1Status === "error" && (
                            <FormControl error>
                                <FormHelperText>
                                    Address 1 should be between 5-60 characters
                                </FormHelperText>
                            </FormControl>
                          )  
                        }
                    </Grid2>
                    <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                        <MaterialBox>
                            <MaterialTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold">
                            Address Line 2
                            </MaterialTypography>
                        </MaterialBox>
                        <MaterialInput
                        name="addressLine2"
                        placeholder="eg. Nandanam"
                        value={formData.addressLine2}
                        onChange={handleChange}
                        success={formData.addressLine2Status === "success"}
                        error={formData.addressLine2Status === "error"}
                        disabled={pageMode === "view"}
                        />
                        {
                          formData.addressLine2Status === "error" && (
                            <FormControl error>
                                <FormHelperText>
                                    Address 2 should be between 0-60 characters
                                </FormHelperText>
                            </FormControl>
                          )  
                        }
                    </Grid2>
                    <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                        <MaterialBox>
                            <MaterialTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold">
                            Landmark
                            </MaterialTypography>
                        </MaterialBox>
                        <MaterialInput
                        name="landmark"
                        value={formData.landmark}
                        onChange={handleChange}
                        success={formData.landmarkStatus === "success"}
                        error={formData.landmarkStatus === "error"}
                        disabled={pageMode === "view"}
                        />
                        {
                          formData.landmarkStatus === "error" && (
                            <FormControl error>
                                <FormHelperText>
                                    Landmark should be between 0-40 characters
                                </FormHelperText>
                            </FormControl>
                          )  
                        }
                    </Grid2>
                    
                </Grid2>
                <Divider/>
                {/* Contact */}
                <Stack direction="column">
                    <Stack direction="row" spacing={1}>
                        <Contacts style={{ fontSize: 20 }} />
                        <MaterialTypography
                            component="label"
                            variant="body2"
                            fontWeight="bold">
                            Contact Information
                        </MaterialTypography>                        
                    </Stack>
                    <MaterialTypography
                    ml={3.5}
                    variant="caption" color="text">
                        Contact Information to be entered only once, cannot be edited once entered
                    </MaterialTypography>
                </Stack>
                {contacts.map((contact,index) => (
                <MaterialBox border="1px solid #e0e0e0" borderRadius="8px" padding="16px" marginTop="8px">
                    <Grid2 container spacing={1}>
                        <Grid2 size={{lg:12, md:12, sm: 12, xs: 12 }}>
                            <MaterialBox
                            display="flex"
                            justifyContent="space-between"
                            >
                                <Stack direction="row" spacing={1}>
                                    <MaterialTypography
                                        component="label"
                                        fontWeight="bold"
                                        fontSize="14px">
                                        {index + 1}. Contact
                                    </MaterialTypography>
                                </Stack>
                                <MaterialBox
                                display="flex"
                                justifyContent="space-between"
                                >
                                    {contacts.length > 1 && (
                                        <IconButton
                                            onClick={() => handleRemoveContact(index)}
                                            disabled={pageMode === "view" || pageMode === "edit"}
                                            size="small"
                                            sx={{
                                            border: "1px solid",
                                            borderRadius: "0px",
                                            px: 1,
                                            color: "error.main",
                                            borderColor: "error.main",
                                            "&:hover": {
                                                backgroundColor: "error.light",
                                                color: "error.contrastText",
                                            },
                                            }}
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    )}
                                    <IconButton
                                        color="dark"
                                        onClick={handleAddContact}
                                        disabled={pageMode === "view" || pageMode === "edit"}
                                        size="small"
                                        sx={{
                                            border: "1px solid",
                                            borderRadius: "0px",
                                            color: "success.main",
                                            borderColor: "success.main",
                                            "&:hover": {
                                                backgroundColor: "#afff9f",
                                                color: "success.contrastText",
                                        }}}
                                    >
                                        <Add /> 
                                        <MaterialTypography variant="caption" fontWeight="medium">Contact</MaterialTypography>
                                    </IconButton>                                    
                                </MaterialBox>
                            </MaterialBox>
                        </Grid2>
                        <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                            <MaterialBox>
                                <MaterialTypography
                                component="label"
                                variant="caption"
                                fontWeight="bold">
                                    Contact Type
                                </MaterialTypography>
                            </MaterialBox>
                            <MaterialSelect
                            name="contactType"
                            value={contacts[index].contactType}
                            onChange={(value) =>
                                handleContactChange(index, value, { name: "contactType" })
                            }
                            options={contactTypeOptions}
                            success={contacts[index].contactTypeStatus === "success"}
                            error={contacts[index].contactTypeStatus === "error"}
                            isDisabled={pageMode === "view"}
                            menuPlacement="top"
                            />
                        </Grid2>
                        {contacts[index]?.contactType !== "" && (
                        <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                            <MaterialBox>
                                <MaterialTypography
                                component="label"
                                variant="caption"
                                fontWeight="bold">
                                    {
                                    contact?.contactType?.label === "Email" ? "Mail ID"
                                    : contact?.contactType?.label === "Website" ? "URL"
                                    : contact?.contactType?.label === "Other" ? "Value"
                                    : "Number"
                                    } *
                                </MaterialTypography>
                            </MaterialBox>
                            <MaterialInput
                            name="value"
                            value={contacts[index].value}
                            onChange={(e) => handleContactChange(index, e)}
                            success={contacts[index].valueStatus === "success"}
                            error={contacts[index].valueStatus === "error"}
                            disabled={pageMode === "view"}
                            />
                            {contacts[index].valueStatus === "error" && (
                                <FormControl error>
                                    <FormHelperText>
                                    {
                                    contact.contactType.label === "Email" ? "Mail ID"
                                    : contact.contactType.label === "Website" ? "URL"
                                    : contact.contactType.label === "Other" ? "Value"
                                    : "Number"
                                    } should be at least 1 character and at most 80 characters long.
                                    </FormHelperText>
                                </FormControl>
                            )}
                        </Grid2>
                        )}
                        <Grid2 size={{lg:2, md:2, sm: 3, xs: 3 }}>
                            <MaterialBox>
                                <MaterialTypography
                                component="label"
                                variant="caption"
                                fontWeight="bold">
                                    Primary
                                </MaterialTypography>
                            </MaterialBox>
                            <FormControlLabel
                            control={
                                <Switch
                                    checked={contacts[index].primary}
                                    disabled={pageMode === "view"}
                                    onChange={(e) => handleContactChange(index, {
                                            target: { name: "primary", value: e.target.checked },
                                        })
                                    }
                                    color="primary"
                                />
                            }
                            label={
                                <MaterialTypography variant="caption" fontWeight="bold">
                                    {contacts?.primary ? "Yes" : "No"}
                                </MaterialTypography>
                            }
                            sx={{ marginLeft: 0 }}
                            />
                        </Grid2>
                        <Grid2 size={{lg:2, md:2, sm: 3, xs: 3 }}>
                            <MaterialBox>
                                <MaterialTypography
                                component="label"
                                variant="caption"
                                fontWeight="bold">
                                    Active
                                </MaterialTypography>
                            </MaterialBox>
                            <FormControlLabel
                            control={
                                <Switch
                                    checked={contacts[index].isActive}
                                    disabled={pageMode === "view"}
                                    onChange={(e) => handleContactChange(index, {
                                            target: { name: "isActive", value: e.target.checked },
                                        })
                                    }
                                    color="primary"
                                />
                            }
                            label={
                                <MaterialTypography variant="caption" fontWeight="bold">
                                    {contacts?.isActive ? "Yes" : "No"}
                                </MaterialTypography>
                            }
                            sx={{ marginLeft: 0 }}
                            />
                        </Grid2>
                        
                    </Grid2>
                </MaterialBox>
                ))}
            </CardContent>
            <Divider/>
            <MaterialBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
            {pageMode === "add" && (
            <>
                <MaterialButton
                sx={{ m: 2 }}
                variant="outlined"
                color="dark"
                onClick={() => navigate("/general-master/bank-branch-list")}
                >
                Cancel
                </MaterialButton>
                <MaterialButton
                sx={{ m: 2 }}
                variant="gradient"
                color="dark"
                disabled={!checkAllFields()}
                onClick={handleSubmitBankBranch}
                >
                Submit
                </MaterialButton>
            </>
            )}

            {pageMode==="edit" && (
            <>
                <MaterialButton
                variant="outlined"
                color="dark"
                sx={{ m: 2 }}
                onClick={() => navigate("/general-master/bank-branch-list")}
                >
                Cancel
                </MaterialButton>
                <MaterialButton
                sx={{ m: 2 }}
                variant="gradient"
                color="dark"
                disabled={!checkAllFields()}
                onClick={handleSubmitBankBranch}
                >
                Update
                </MaterialButton>
            </>
            )}
            </MaterialBox>
        </Card>
    </DashboardLayout>
  )
}

export default BankBranchForm